import { NCFileSearchItem, NCFileVersion as NCFileVersionType } from '@interfaces/search';
export type { NCFileVersionType };

import { Machine } from '@interfaces/machines';
import { ApplyNcFileVersionMasterDataChanges, GetNCFileById } from '@interfaces/methods';
import { Dispatch, SetStateAction } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { SimpleOnPress } from '@interfaces/base';
import { WithColor } from '@styles/colors';

export interface NCFileVersionsProps extends WithColor {
  machines: Machine[];
  ncFile: NCFileSearchItem | null;
  ncFileId?: number;
  loaded: boolean;
  enableResponsiveTwoColumnView?: boolean;
  twoColumnBreakpoint?: number;
  mobileBreakpoint?: number;
  forceTwoColumnOnNativeTablet?: boolean;
  machineId?: number;
  shouldDisplayFilterInactiveVersionsButton?: boolean;
  showOnlyActiveVersions?: boolean;
  selectedItem: Machine;
  applyNcFileVersionMasterDataChanges: ApplyNcFileVersionMasterDataChanges;
  setNcFileData: (ncFile: NCFileSearchItem) => void;
  getNCFileById: GetNCFileById;
  getNCFile?: (hash: string, fullFile: boolean) => Promise<{ data: string; size: number }>;
  twoColumnView?: boolean;
  shouldLoadNcFile?: boolean;
}

export interface NCFileVersionSelectorProps extends Pick<NCFileVersionsProps, 'C'> {
  selected: boolean;
  isFavorite: boolean;
  version: NCFileVersionType;
  onPress: (selectedVersion: NCFileVersionType) => void;
  onToggleFavorite?: (version: NCFileVersionType) => void;
  withShadow?: boolean;
  roundTop?: boolean;
  roundBottom?: boolean;
  bottomLine?: boolean;
  withFavoriteIcon?: boolean;
  withSelectionIndicator?: boolean;
}

export interface ActionButtonProps extends Pick<NCFileVersionsProps, 'C'> {
  loading: boolean;
  text: string;
  borderBottomRadius: boolean;
  borderTopRadius: boolean;
  action: () => void;
}

export interface LeftColumnProps extends Pick<NCFileVersionsProps, 'C'> {
  favoriteOrLatest: NCFileVersionType;
  favorite: NCFileVersionType | undefined;
  selectedVersion: NCFileVersionType;
  onSelectVersion?: (version: NCFileVersionType) => void;
  forceFullWidth?: boolean;
  machineWhichRanFavoriteOrLatest?: Machine;
  otherVersions: NCFileVersionType[] | undefined;
  shouldDisplayFilterInactiveVersionsButton?: boolean;
  showOnlyActiveVersions: boolean;
  setShowOnlyActiveVersions?: Dispatch<SetStateAction<boolean>>;
  inactiveVersionsCount?: number;
  // Comparison mode props
  comparisonMode?: boolean;
  versionsToCompare?: NCFileVersionType[];
  onExitComparisonMode?: () => void;
  onToggleVersionForComparison?: (version: NCFileVersionType) => void;
  onStartComparison?: () => void;
  onToggleFavoriteVersion?: (version: NCFileVersionType) => void;
  // Upload new version
  onUploadNewVersion?: () => void;
  // Narrow mode: render compare card inline in LeftColumn
  showCompareCardInline?: boolean;
  onEnterComparisonMode?: (initialVersion?: NCFileVersionType) => void;
  // Caps/styles the inner ScrollView so the list can scroll inside a content-sized container (e.g. the version picker popup).
  scrollStyle?: StyleProp<ViewStyle>;
}

export interface CentralColumnProps extends Pick<LeftColumnProps, 'C'>, Pick<NCFileVersionsProps, 'twoColumnView'> {
  selectedVersion: NCFileVersionType;
  ncFile: NCFileSearchItem;
  visible: boolean;
  onSendToMachine: () => void;
  onMarkThisVersionAsInactive: () => Promise<void>;
  comparisonMode?: boolean;
  onEnterComparisonMode?: (initialVersion?: NCFileVersionType) => void;
  hasMultipleVersions?: boolean;
  versionsToCompare?: NCFileVersionType[];
  onExitComparisonMode?: () => void;
  onStartComparison?: () => void;
}

export interface RightColumnProps extends Pick<NCFileVersionsProps, 'selectedItem' | 'C'>, Pick<CentralColumnProps, 'visible'> {
  ncFile: NCFileSearchItem;
  selectedVersion: NCFileVersionType;
  onSendToMachine?: () => void;
}

export enum RightColumnLevel {
  Info = 'Information',
  Code = 'NC Code',
}

export interface ScrollToTopButtonProps extends SimpleOnPress, WithColor {
  visible: boolean;
}

export interface UseManageNCFileVersionsProps
  extends Pick<
    NCFileVersionsProps,
    | 'C'
    | 'ncFile'
    | 'ncFileId'
    | 'machineId'
    | 'shouldLoadNcFile'
    | 'enableResponsiveTwoColumnView'
    | 'twoColumnBreakpoint'
    | 'mobileBreakpoint'
    | 'forceTwoColumnOnNativeTablet'
    | 'twoColumnView'
  > {
  ncFileVersionIdToSelect?: number;
  showOnlyActiveVersionsInitialState?: boolean;
}

export interface UseUploadNcFileVersionParams extends Pick<NCFileVersionsProps, 'ncFile'> {}

export interface CompareVersionsBlockProps
  extends WithColor,
    Pick<CentralColumnProps, 'selectedVersion'>,
    Required<Pick<CentralColumnProps, 'onEnterComparisonMode'>> {}

export interface FileInfoBlockProps extends WithColor, Pick<CentralColumnProps, 'selectedVersion' | 'ncFile'> {}
